export interface ApiSuccess<T> {
  code: 10000;
  message: string;
  data: T;
  traceId: string;
}

export interface ApiFailure {
  code: number;
  message: string;
  data: null;
  traceId: string;
}

export type ApiResult<T> = ApiSuccess<T> | ApiFailure;
export type ApiErrorKind = 'timeout' | 'network' | 'auth' | 'business' | 'server';

const apiBaseUrl = (import.meta.env.VITE_API_BASE_URL ?? '/api').replace(/\/$/, '');

export class ApiRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly traceId?: string,
    readonly kind: ApiErrorKind = 'server',
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

export async function apiRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 12_000);
  const abortFromCaller = () => controller.abort();
  init?.signal?.addEventListener('abort', abortFromCaller, { once: true });
  let response: Response;
  try {
    response = await fetch(`${apiBaseUrl}${path}`, {
      ...init,
      signal: controller.signal,
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...init?.headers,
      },
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new ApiRequestError('请求超时，尚不能确认服务端是否已处理。', 0, undefined, 'timeout');
    }
    throw new ApiRequestError('网络连接失败，请检查连接后重试。', 0, undefined, 'network');
  } finally {
    window.clearTimeout(timeout);
    init?.signal?.removeEventListener('abort', abortFromCaller);
  }

  const payload = (await response.json().catch(() => null)) as ApiResult<T> | null;
  if (!response.ok || !payload || payload.code !== 10000 || payload.data === null) {
    const kind: ApiErrorKind = response.status === 401 ? 'auth' : response.status >= 400 && response.status < 500 ? 'business' : 'server';
    const message = response.status === 401 ? '登录状态已失效，请重新建立测试游客账户。' : payload?.message ?? '服务返回了无法识别的错误。';
    throw new ApiRequestError(message, response.status, payload?.traceId, kind);
  }

  return payload.data;
}

export function jsonRequest<T>(path: string, method: 'POST' | 'PUT', body?: unknown, headers?: HeadersInit) {
  return apiRequest<T>(path, {
    method,
    body: body === undefined ? undefined : JSON.stringify(body),
    headers,
  });
}
